import { TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import { Alert, Button, cn } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  queryOptionsGetTaskFilterOptions,
  queryOptionsGetTasksPaginated,
} from '../../../api/task/taskApi';
import {
  AppPaginationFooter,
  type AppPaginationFooterProps,
} from '../../../components/AppPagination';
import { AppSearchInput } from '../../../components/AppSearchInput';
import { DownloadCsvButton } from '../../../components/DownloadCsvButton';
import { EntityList } from '../../../components/EntityList';
import { ListToolbar } from '../../../components/ListToolbar';
import { PageHeaderActions } from '../../../components/PageHeaderActionsSlot';
import { SnapshotListSkeleton } from '../../../components/SnapshotListSkeleton';
import {
  TaskCard,
  TaskCardSkeleton,
  buildTaskCardViewUrl,
} from '../../../components/TaskCard';
import { SEARCH_KEYS } from '../../../constant/SearchKeys';
import { useActiveWorkspace } from '../../../hooks/useActiveWorkspace';
import { useDebouncedUrlParam } from '../../../hooks/useDebouncedUrlParam';
import { usePaginationBounds } from '../../../hooks/usePaginationBounds';
import { useTaskPartyNames } from '../../../hooks/useTaskPartyNames';
import { WorkspaceTask } from '../../../types/taskTypes';
import { isCsvExportDisabled } from '../../../utils/csv/csvExport';
import { workspaceTypeIsAgent } from '../../../utils/helpers/common';
import { TasksFilterBar } from './TasksFilterBar';
import { TasksSortControls } from './TasksSortControls';
import { TASK_FILTER_PARAMS, TASKS_PAGE_PARAM } from './taskFilterOptions';
import { taskTabByType } from './taskTabs';
import {
  TASK_EXPORT_PAGE,
  fetchTasksCsvPage,
  tasksCsvColumns,
  tasksCsvContext,
} from './tasksCsvSpec';
import { useTaskFilters } from './useTaskFilters';
import { useTaskSort } from './useTaskSort';
import { useWriteTaskParams } from './useWriteTaskParams';

const TASKS_PAGE_SIZE = 10;

interface TasksTabPageProps {
  type: TaskTypeEnum;
}

/**
 * Content for a single Tasks-page tab: a debounced search box, a paginated card
 * list reusing the shared {@link TaskCard}, and loading / empty states. The
 * search term is filtered server-side via the task list endpoint's `searchTerm`
 * param; pagination is server-side too.
 */
export const TasksTabPage = ({ type }: TasksTabPageProps) => {
  const { workspaceId } = useParams();
  const { search: locationSearch } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = taskTabByType(type);
  const [activeWorkspace, activeWorkspaceQuery] = useActiveWorkspace();
  const isAgent = workspaceTypeIsAgent(activeWorkspace);
  const filterOptionsQuery = useQuery(
    queryOptionsGetTaskFilterOptions({ type, workspaceId }),
  );

  const taskFilters = useTaskFilters({
    isClientFilterAvailable: isAgent,
    otherClaimantOptions: filterOptionsQuery.data?.otherClaimants,
  });

  // `tasksClient` only applies to agents, so a URL carrying one waits for the
  // workspace type rather than listing every client's tasks first and then
  // swapping the list once the workspace loads.
  const isAwaitingClientFilter =
    activeWorkspaceQuery.isPending &&
    searchParams.has(TASK_FILTER_PARAMS.client);

  const search = searchParams.get(SEARCH_KEYS.TASKS_SEARCH) ?? '';
  const page = Math.max(1, Number(searchParams.get(TASKS_PAGE_PARAM)) || 1);
  const taskSort = useTaskSort(tab?.sort);
  const writeParams = useWriteTaskParams();

  const setSearch = useCallback(
    (value: string) => writeParams({ [SEARCH_KEYS.TASKS_SEARCH]: value }),
    [writeParams],
  );

  const setPage = (nextPage: number) =>
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);

        updated.set(TASKS_PAGE_PARAM, String(nextPage));

        return updated;
      },
      { replace: true },
    );

  // The filters alone: the CSV fetcher supplies its own page, pageSize and sort
  // tiebreaker, so the export matches the list on screen at Download time.
  const tasksParams = {
    ...taskFilters.queryFilters,
    searchTerm: search.trim() || undefined,
    sortBy: taskSort.sortBy,
    type,
    workspaceId,
  };

  const tasksQuery = useQuery({
    ...queryOptionsGetTasksPaginated({
      ...tasksParams,
      page,
      pageSize: TASKS_PAGE_SIZE,
    }),
    enabled: !isAwaitingClientFilter,
  });

  // Local input + debounced URL commit; queryStatus drives the search spinner.
  const {
    input: searchInput,
    isSearchFetching = false,
    setInput: setSearchInput,
  } = useDebouncedUrlParam(search, setSearch, {
    queryStatus: {
      isFetching: tasksQuery.isFetching,
      isPending: tasksQuery.isPending,
    },
  });

  const tasks = (tasksQuery.data?.items as WorkspaceTask[]) ?? [];
  const meta = tasksQuery.data?.meta ?? null;
  const totalPages = meta?.totalPages ?? 0;
  const totalItems = meta?.totalItems ?? 0;

  const { clampedPage, isPageOutOfRange } = usePaginationBounds({
    page,
    setPage,
    totalPages,
  });

  // Client and claimant names come from secondary requests. With `type` given,
  // the filter options name them without waiting on the tasks. The Resolved
  // filter lists completed claims, so the options have to cover those too.
  const { getPartyNames, isWorkspaceTypePending } = useTaskPartyNames(
    tasks,
    workspaceId,
    { includeResolved: taskFilters.queryFilters.includeResolved, type },
  );

  const trimmedSearch = search.trim();

  // A tab with no tasks and no active search or filter has nothing to page,
  // search or narrow.
  const hasNothingToSearch =
    tasks.length === 0 &&
    !trimmedSearch &&
    !taskFilters.hasFilterSelection &&
    !tasksQuery.isError;

  // Pagination is meaningless while pending, on error, or on an empty tab.
  const areControlsDisabled =
    tasksQuery.isPending || tasksQuery.isError || hasNothingToSearch;

  // The search field stays usable on error so the user can change the term and
  // retry instead of being locked out with no way to recover.
  const isSearchDisabled = tasksQuery.isPending || hasNothingToSearch;

  const paginationProps: AppPaginationFooterProps = {
    currentPage: clampedPage,
    disabled: areControlsDisabled,
    onPageChange: setPage,
    pageSize: TASKS_PAGE_SIZE,
    totalItems,
    totalPages,
  };

  // `keepPreviousData` holds the last list on screen while a new sort, filter or
  // page loads, so dim it rather than let it pass for the new result.
  const isShowingPreviousData = tasksQuery.isPlaceholderData;

  const skeleton = (
    <SnapshotListSkeleton
      label="Loading tasks"
      numCards={TASKS_PAGE_SIZE}
      renderCard={() => <TaskCardSkeleton type={type} />}
    />
  );

  const exportPage = TASK_EXPORT_PAGE[type];

  const renderContent = () => {
    if (
      tasksQuery.isLoading ||
      isAwaitingClientFilter ||
      isWorkspaceTypePending
    ) {
      return skeleton;
    }

    if (tasksQuery.isError) {
      return (
        <EntityList.Empty
          title="Couldn’t load tasks"
          description="Something went wrong loading your tasks. Please try again."
          iconProps={{ icon: 'warning' }}
        />
      );
    }

    if (tasks.length === 0) {
      // Empty only because the page is stale — the snap-back refetch is queued,
      // so show the skeleton instead of a misleading "no tasks" message.
      if (isPageOutOfRange) {
        return skeleton;
      }

      if (taskFilters.hasFilterSelection) {
        return (
          <EntityList.Empty
            title="No tasks match these filters"
            description="Try a different filter, or clear them to see every task."
            iconProps={{ icon: 'search' }}
          >
            <Button variant="outline" onClick={taskFilters.clearFilters}>
              Clear filters
            </Button>
          </EntityList.Empty>
        );
      }

      return trimmedSearch ? (
        <EntityList.Empty
          title="No results found"
          description={`No tasks match “${trimmedSearch}”.`}
          iconProps={{ icon: 'search' }}
        />
      ) : (
        <EntityList.Empty
          title={tab?.emptyTitle}
          description={tab?.emptyDescription}
        />
      );
    }

    return (
      <EntityList>
        <EntityList.Content>
          {tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              type={type}
              {...getPartyNames(task)}
              viewUrl={buildTaskCardViewUrl(task, type, locationSearch)}
              workspaceId={workspaceId}
            />
          ))}
        </EntityList.Content>
      </EntityList>
    );
  };

  return (
    <div className="space-y-4">
      {exportPage && (
        <PageHeaderActions>
          <DownloadCsvButton
            key={type}
            label={tab?.label ?? ''}
            columns={tasksCsvColumns(type)}
            createResolveContext={() =>
              tasksCsvContext(
                workspaceId ?? '',
                type,
                isAgent,
                taskFilters.queryFilters.includeResolved,
              )
            }
            fetchPage={(exportPageNo, pageSize, signal) =>
              fetchTasksCsvPage(tasksParams, exportPageNo, pageSize, signal)
            }
            fileLabel={exportPage}
            isDisabled={
              isWorkspaceTypePending ||
              isCsvExportDisabled(tasksQuery, totalItems)
            }
            rowCount={totalItems}
            scope="section"
          />
        </PageHeaderActions>
      )}

      {tab?.notice && (
        <Alert title={tab.notice.title} variant="info">
          {tab.notice.description}
        </Alert>
      )}

      {tab && (
        <TasksFilterBar
          disabled={areControlsDisabled}
          filterOptions={filterOptionsQuery.data}
          filtersDisabled={hasNothingToSearch}
          isAgent={isAgent}
          search={
            <AppSearchInput
              className="text-sm"
              classes={{
                // bg on the wrapper — opaque bg on the input covers section icons
                wrapper: 'sm:w-64 rounded-md bg-white',
              }}
              disabled={isSearchDisabled}
              isFetching={isSearchFetching}
              placeholder={tab.searchPlaceholder}
              value={searchInput}
              onChange={setSearchInput}
            />
          }
          tab={tab}
          taskFilters={taskFilters}
        />
      )}

      <ListToolbar
        {...paginationProps}
        sort={
          tab &&
          taskSort.selection && (
            <TasksSortControls
              disabled={areControlsDisabled}
              fields={tab.sort.fields}
              selection={taskSort.selection}
              onSelectionChange={taskSort.setSelection}
            />
          )
        }
      />

      <div
        aria-busy={isShowingPreviousData}
        className={cn(
          'transition-opacity',
          isShowingPreviousData && 'opacity-60',
        )}
      >
        {renderContent()}
      </div>

      <AppPaginationFooter {...paginationProps} />
    </div>
  );
};
